/**
 * Company Goals — the portal that replaces Jamie's Monday email.
 *
 * Pace comes from the shared goal engine (unique company GP). Rankings are
 * grouped by role. Sales + Recruiting are two views of the same GP.
 */

import { useState } from 'react';

import {
  api,
  type GoalPortalData,
  type GoalRanking,
  type ScoreboardRanking,
  type WeeklyScoreboardData,
} from '../api';
import { MiniSparkline } from '../components/MiniSparkline';
import { Modal } from '../components/Modal';
import { UnifiedTimeFilter } from '../components/UnifiedTimeFilter';
import { useTimeFilter } from '../context/TimeFilterContext';
import { Card, CardHeader, ExportButton, Spinner } from '../components/Ui';
import { useAsyncData } from '../hooks/useAsyncData';
import { hours, moneyCompact, shortDate } from '../format';
import { Shell, type ShellProps } from '../Shell';
import { GoalProgress } from './GoalProgress';
import { GoalWeekTable } from './GoalWeekTable';
import { ReportShell } from './ReportShell';

const MEDAL = ['🥇', '🥈', '🥉'] as const;

const ROLE_SECTIONS = [
  { role: 'salesperson', title: 'Sales' },
  { role: 'recruiter', title: 'Recruiting' },
] as const;

export function WeeklyScoreboard(props: ShellProps) {
  const tf = useTimeFilter();
  const [notesOpen, setNotesOpen] = useState(false);
  const canWrite = props.session.permissions.includes('config:write');
  const ownUserId = props.session.user.id;

  const {
    data: goals,
    error: goalsError,
    loading: goalsLoading,
    reload: reloadGoals,
  } = useAsyncData<GoalPortalData>(
    () => api.goalPortal({ subject: 'company', weekEnding: tf.toWeekEnding() }),
    [tf.selection],
    'Could not load company goals.',
  );

  const {
    data: board,
    error: boardError,
    loading: boardLoading,
  } = useAsyncData<WeeklyScoreboardData>(
    () => api.weeklyScoreboard(tf.toWeekEnding()),
    [tf.selection],
    'Could not load weekly rankings.',
  );

  const weekEnding =
    tf.toWeekEnding() ?? goals?.progress?.asOfWeekEnding ?? board?.weekEnding ?? null;
  const loading = goalsLoading || boardLoading;
  const error = goalsError ?? boardError;

  const detailById = new Map((board?.rankings ?? []).map((r) => [r.userId, r]));

  return (
    <Shell {...props}>
      <ReportShell
        title="Company Goals"
        subtitle="Annual GP pace, Sales and Recruiting, announcements"
        weekEnding={weekEnding}
        actions={
          <>
            {canWrite && weekEnding !== null && !goals?.missingPlan && (
              <button
                type="button"
                onClick={() => setNotesOpen(true)}
                className="print:hidden inline-flex items-center rounded-lg border border-edge px-3 py-1.5 text-xs font-medium text-ink-muted hover:text-ink"
              >
                Add announcement
              </button>
            )}
            {goals?.progress !== null && goals !== null && (
              <ExportButton
                data={goals.progress.weeks.map((w) => ({
                  week: w.weekNumber,
                  ending: w.weekEnding,
                  actual: w.actual ?? '',
                  target: w.weeklyTarget,
                  variance: w.variance ?? '',
                  ytd: w.ytd,
                  remaining: w.goalRemaining,
                }))}
                filename={`company-goals-${weekEnding ?? 'latest'}`}
                headers={[
                  { key: 'week', label: 'Week' },
                  { key: 'ending', label: 'Week ending' },
                  { key: 'actual', label: 'Actual' },
                  { key: 'target', label: 'Target' },
                  { key: 'variance', label: 'Variance' },
                  { key: 'ytd', label: 'YTD' },
                  { key: 'remaining', label: 'Remaining' },
                ]}
              />
            )}
            <button
              type="button"
              onClick={() => window.print()}
              className="print:hidden rounded-lg border border-edge px-3 py-1.5 text-xs font-medium text-ink-muted hover:text-ink"
            >
              Print
            </button>
            <UnifiedTimeFilter />
          </>
        }
      >
        {error && <p className="text-sm text-red-600">{error}</p>}
        {loading && (
          <div className="flex justify-center py-12">
            <Spinner label="Loading company goals…" />
          </div>
        )}

        {goals !== null && !loading && (
          <>
            {goals.missingPlan || goals.progress === null ? (
              <Card>
                <p className="px-4 py-8 text-center text-sm text-ink-muted">
                  No company goal plan for {String(goals.year)}. Set one in Admin → Goals.
                </p>
              </Card>
            ) : (
              <>
                <GoalProgress
                  progress={goals.progress}
                  title={`${String(goals.year)} company goal`}
                />
                <p className="mb-5 text-xs text-ink-faint">
                  Company GP is unique contractor profit. Sales and Recruiting lists are two views
                  of the same week and do not add up to the company total.
                </p>

                {goals.milestones.length > 0 && (
                  <Card className="mb-5 border-l-4 border-l-profit">
                    <div className="px-4 py-3">
                      <p className="text-sm font-semibold text-ink">Milestones this week</p>
                      <ul className="mt-1 list-disc pl-5 text-sm text-ink-muted">
                        {goals.milestones.map((m) => (
                          <li key={`${m.kind}-${m.label}`}>{m.label}</li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                )}

                {goals.notes.length > 0 && (
                  <NotesPanel notes={goals.notes} weekEnding={goals.progress.asOfWeekEnding} />
                )}

                <div className="grid items-start gap-4 print:grid-cols-1 lg:grid-cols-[minmax(17.5rem,22rem)_minmax(0,1fr)]">
                  <div className="flex min-w-0 flex-col gap-3">
                    {ROLE_SECTIONS.map(({ role, title }) => {
                      const rows = goals.rankings.filter((r) => r.role === role);
                      const top = rows[0];
                      return (
                        <RoleSection
                          key={role}
                          title={title}
                          rows={rows}
                          top={top}
                          detailById={detailById}
                          ownUserId={ownUserId}
                          navigate={props.navigate}
                        />
                      );
                    })}
                  </div>
                  <GoalWeekTable
                    weeks={goals.progress.weeks}
                    asOfWeekNumber={goals.progress.asOfWeekNumber}
                    compact
                  />
                </div>
              </>
            )}
          </>
        )}

        {weekEnding !== null && (
          <NotesDialog
            open={notesOpen}
            onClose={() => setNotesOpen(false)}
            weekEnding={weekEnding}
            onSaved={() => {
              setNotesOpen(false);
              reloadGoals();
            }}
          />
        )}
      </ReportShell>
    </Shell>
  );
}

function RoleSection({
  title,
  rows,
  top,
  detailById,
  ownUserId,
  navigate,
}: {
  title: string;
  rows: readonly GoalRanking[];
  top: GoalRanking | undefined;
  detailById: Map<string, ScoreboardRanking>;
  ownUserId: string;
  navigate: (hash: string) => void;
}) {
  return (
    <Card>
      <CardHeader
        compact
        title={title}
        subtitle={
          top === undefined
            ? 'No allocations this week'
            : `By weekly GP · ${top.name} ${moneyCompact(top.weeklyGp)}`
        }
      />
      {rows.length === 0 ? (
        <p className="px-3 pb-3 text-sm text-ink-muted">No people with allocated GP this week.</p>
      ) : (
        <div className="space-y-1.5 px-2 pb-2">
          {rows.map((row, i) => (
            <ScoreboardRow
              key={row.userId}
              row={row}
              rank={i + 1}
              detail={detailById.get(row.userId)}
              isOwn={ownUserId === row.userId}
              onOpenOwn={() => navigate('my-commissions')}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function ScoreboardRow({
  row,
  rank,
  detail,
  isOwn,
  onOpenOwn,
}: {
  row: GoalRanking;
  rank: number;
  detail: ScoreboardRanking | undefined;
  isOwn: boolean;
  onOpenOwn: () => void;
}) {
  const medal = rank <= 3 ? MEDAL[rank - 1] : null;
  const delta =
    detail?.weeklyDelta === undefined || detail.weeklyDelta === null ? null : detail.weeklyDelta;
  const deltaTone =
    delta === null ? 'text-ink-faint' : delta.startsWith('-') ? 'text-red-500' : 'text-profit';

  return (
    <div className="flex items-center gap-2 rounded-lg border border-edge px-2.5 py-1.5">
      <div className="flex w-6 shrink-0 items-center justify-center text-sm font-bold text-ink">
        {medal ?? rank}
      </div>
      <div className="min-w-0 flex-1">
        {isOwn ? (
          <button
            type="button"
            onClick={onOpenOwn}
            className="block truncate text-sm font-semibold text-profit hover:underline"
          >
            {row.name}
          </button>
        ) : (
          <p className="truncate text-sm font-semibold text-ink">{row.name}</p>
        )}
        {detail !== undefined && (
          <p className="truncate text-[11px] text-ink-faint">
            {hours(detail.hours)}h · {detail.contractors} ctr
            {detail.contractors === 1 ? '' : 's'}
            {delta !== null && <span className={deltaTone}> · {delta}%</span>}
          </p>
        )}
      </div>
      {detail !== undefined && (
        <MiniSparkline values={detail.sparkline} width={56} height={20} color="#16a34a" />
      )}
      <div className="shrink-0 text-right">
        <p className="figure text-sm font-bold text-profit">{moneyCompact(row.weeklyGp)}</p>
        <p className="figure text-[11px] text-ink-faint">{moneyCompact(row.ytdGp)} YTD</p>
      </div>
    </div>
  );
}

function NotesPanel({ notes, weekEnding }: { notes: GoalPortalData['notes']; weekEnding: string }) {
  return (
    <Card className="mb-4">
      <CardHeader
        compact
        title="Announcements and kudos"
        subtitle={`Week ending ${shortDate(weekEnding)}`}
      />
      <ul className="space-y-2 px-3 pb-3">
        {notes.map((note) => (
          <li key={note.id} className="rounded-lg bg-surface-overlay px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-ink-faint">
              {note.kind}
              {note.author !== null ? ` · ${note.author}` : ''}
            </p>
            <p className="text-sm text-ink">{note.body}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function NotesDialog({
  open,
  onClose,
  weekEnding,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  weekEnding: string;
  onSaved: () => void;
}) {
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<'comment' | 'kudos'>('comment');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (body.trim() === '') return;
    setSaving(true);
    setError('');
    try {
      await api.saveScoreboardNote({ weekEnding, body: body.trim(), kind });
      setBody('');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add announcement"
      subtitle={`Week ending ${shortDate(weekEnding)}`}
    >
      <form onSubmit={(e) => void submit(e)} className="space-y-3">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value === 'kudos' ? 'kudos' : 'comment')}
          className="rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm"
        >
          <option value="comment">Comment</option>
          <option value="kudos">Kudos</option>
        </select>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a note for this week…"
          rows={4}
          className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm"
        />
        <div className="flex items-center justify-end gap-2">
          {error !== '' && <p className="mr-auto text-sm text-caution">{error}</p>}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-edge px-3 py-1.5 text-sm text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || body.trim() === ''}
            className="rounded-lg bg-profit px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Post'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
